import { autoLayout, reasoningGraphLinks } from "@docit/flow-editor";
import {
  Background,
  Controls,
  type Edge,
  type Node,
  type NodeChange,
  ReactFlow,
} from "@xyflow/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ReasoningSession } from "~/api/reasoningTypes";
import { Button } from "~/foundation/components/Button";
import { type CandidateBase, type CandidateDraft, nextRevision } from "./candidateDraft";
import "@xyflow/react/dist/style.css";

export const DRAFT_NODE = "reasoning:draft";
export function canConnectDraft(
  session: ReasoningSession,
  source: string | null,
  target: string | null,
) {
  return (
    target === DRAFT_NODE &&
    session.records.some((row) => row.id === source && row.kind === "candidate" && row.eligible)
  );
}

/** The retained records as a graph; the only writable edges point into the new draft revision. */
export function ReasoningRecordGraph({
  base,
  draft,
  disabled,
  onParentsChange,
  onInspect,
}: {
  base: CandidateBase;
  draft: CandidateDraft;
  disabled: boolean;
  onParentsChange: (ids: string[]) => void;
  onInspect: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [positions, setPositions] = useState<Record<string, { x: number; y: number }>>({});
  const revision = nextRevision(base, draft);
  const edges = useMemo<Edge[]>(
    () => [
      {
        id: "draft:context",
        source: base.session.contextRecordId,
        target: DRAFT_NODE,
        label: t("pinned context"),
        type: "smoothstep",
        deletable: false,
        style: { stroke: "var(--accent)", strokeDasharray: "5 4" },
      },
      ...reasoningGraphLinks(base.session.records).map((link) => ({
        ...link,
        type: "smoothstep",
        deletable: false,
        style: {
          stroke: link.invalidation ? "var(--danger)" : "var(--text-secondary)",
          strokeDasharray: link.invalidation ? "5 4" : undefined,
        },
        labelStyle: { fill: "var(--text)" },
        labelBgStyle: { fill: "var(--bg)" },
      })),
      ...draft.derivedFromRecordIds
        .filter((id) => base.session.records.some((row) => row.id === id))
        .map((id) => ({
          id: `draft:${id}`,
          source: id,
          target: DRAFT_NODE,
          type: "smoothstep",
          deletable: false,
          label: id === base.predecessorId ? t("predecessor") : t("draft parent"),
          style: { stroke: "var(--accent)", strokeWidth: 2, strokeDasharray: "5 4" },
          labelStyle: { fill: "var(--text)" },
          labelBgStyle: { fill: "var(--bg)" },
        })),
    ],
    [base, draft.derivedFromRecordIds, t],
  );
  const nodes = useMemo(() => {
    const records: Node[] = base.session.records.map((row) => ({
      id: row.id,
      position: { x: 0, y: 0 },
      type: "default",
      deletable: false,
      connectable: !disabled && row.kind === "candidate" && row.eligible,
      ariaLabel: t("{{kind}} {{name}} revision {{revision}}", {
        kind: row.kind,
        name: row.logicalId,
        revision: row.revision,
      }),
      data: {
        label: (
          <span>
            <strong>{row.kind}</strong>
            <br />
            {row.logicalId} · r{row.revision}
            {!row.eligible && (
              <>
                <br />
                {t("Needs review")}
              </>
            )}
            {row.id === base.session.currentAcceptanceRecordId && (
              <>
                <br />
                {t("Current acceptance")}
              </>
            )}
          </span>
        ),
      },
      style: {
        width: 210,
        background: "var(--bg)",
        color: "var(--text)",
        border: `2px ${row.eligible ? "solid" : "dashed"} ${row.id === base.predecessorId ? "var(--accent)" : "var(--divider)"}`,
        borderRadius: 8,
        textAlign: "start",
      },
    }));
    records.push({
      id: DRAFT_NODE,
      position: { x: 0, y: 0 },
      type: "default",
      deletable: false,
      connectable: !disabled,
      ariaLabel: t("Draft candidate"),
      data: {
        label: (
          <span>
            <strong>{t("Draft candidate")}</strong>
            <br />
            {draft.logicalId} · r{revision}
          </span>
        ),
      },
      style: {
        width: 210,
        background: "var(--bg-secondary)",
        color: "var(--text)",
        border: "2px dashed var(--accent)",
        borderRadius: 8,
        textAlign: "start",
      },
    });
    return autoLayout(records, edges, "LR").map((node) => ({
      ...node,
      position: positions[node.id] ?? node.position,
    }));
  }, [base, draft.logicalId, revision, disabled, edges, positions, t]);
  function moveNodes(changes: NodeChange[]) {
    if (!changes.some((change) => change.type === "position" && change.position)) return;
    setPositions((current) => {
      const next = { ...current };
      for (const change of changes)
        if (change.type === "position" && change.position) next[change.id] = change.position;
      return next;
    });
  }
  return (
    <section aria-label={t("Reasoning record graph")} className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-small">
          {t("Connect a candidate to the draft to add a parent. Select a record to inspect it.")}
        </p>
        <Button size="sm" onClick={() => setPositions({})}>
          {t("Arrange graph")}
        </Button>
      </div>
      <div className="h-[460px] rounded border border-divider" dir="ltr">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={moveNodes}
          onNodeClick={(_, node) => {
            if (node.id !== DRAFT_NODE) onInspect(node.id);
          }}
          onConnect={(connection) => {
            if (
              !disabled &&
              canConnectDraft(base.session, connection.source, connection.target) &&
              connection.source
            )
              onParentsChange([...new Set([...draft.derivedFromRecordIds, connection.source])]);
          }}
          isValidConnection={(connection) =>
            !disabled && canConnectDraft(base.session, connection.source, connection.target)
          }
          deleteKeyCode={null}
          edgesReconnectable={false}
          fitView
          minZoom={0.15}
          maxZoom={2}
        >
          <Background />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>
    </section>
  );
}
